import { useState } from "react";
import { useRule } from "../CreateRuleNew/RuleReducer";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { FloatLabel } from "primereact/floatlabel";
import { Divider } from "primereact/divider";
import classes from "./RuleWizard.module.css";

const conditionPinStateLabel = (cond, isLock, isTimeTrigger, triggerTime) => {
    const prefix    = cond.is_absence_condition ? "NOT " : "";
    const still      = isTimeTrigger ? "still " : "";
    const onLabel   = isLock ? "closed" : "armed";
    const offLabel  = isLock ? "open"   : "disarmed";
    const timeSuffix = isTimeTrigger && triggerTime ? ` at ${triggerTime.slice(0, 5)}` : "";
    switch (cond.pin_state) {
        case "ARMED":    return `${prefix}${still}${onLabel}${timeSuffix}`;
        case "DISARMED": return `${prefix}${still}${offLabel}${timeSuffix}`;
        case "ANY":      return `${onLabel} or ${offLabel}${timeSuffix}`;
        case "PULSE":    return `pulsed${timeSuffix}`;
        default:         return `${prefix}${cond.pin_state || "?"}${timeSuffix}`;
    }
};

const controlTypeLabel = (ct) => {
    const map = { ARM: "arm", TEMP_ARM: "temporarily arm", DISARM: "disarm", TEMP_DISARM: "temporarily disarm", OPPOSE: "oppose", TOGGLE: "toggle", MATCH: "match", PULSE: "pulse" };
    return map[ct] ?? ct?.toLowerCase() ?? "—";
};

const WizardStepRuleInfo = ({ onBack, onCreate }) => {
    const { state, dispatch } = useRule();
    const defaultName = state.isGeofence ? `${state.geofenceDirection} geofence rule` : (state.name || "");
    const [name, setName] = useState(defaultName);
    const [description, setDescription] = useState(state.description || "");
    const [creating, setCreating] = useState(false);

    const handleCreate = async () => {
        if (!name.trim()) return;
        dispatch({ type: "SET_RULE_NAME", payload: name });
        dispatch({ type: "SET_RULE_DESCRIPTION", payload: description });
        setCreating(true);
        await onCreate({ ...state, name, description });
        setCreating(false);
    };

    const controlActions = state.actions.filter(a => a.action_type === "CONTROL_OUTPUT");
    const hasAlert = state.actions.some(a => a.action_type === "SEND_ALERT");

    return (
        <>
            <div className={classes.contentHeader}>
                <h2 className={classes.contentTitle}>Rule info</h2>
                <p className={classes.contentSubtitle}>Give this rule a name and review the summary before creating it.</p>
            </div>

            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "18px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                    <FloatLabel>
                        <InputText
                            id="wizard-rule-name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            style={{ width: "100%" }}
                        />
                        <label htmlFor="wizard-rule-name">Rule name *</label>
                    </FloatLabel>
                    <FloatLabel>
                        <InputTextarea
                            id="wizard-rule-desc"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            rows={3}
                            style={{ width: "100%" }}
                        />
                        <label htmlFor="wizard-rule-desc">Description (optional)</label>
                    </FloatLabel>
                </div>

                <Divider />

                {/* Summary */}
                <div style={{ background: "#f8f9fa", borderRadius: "8px", padding: "14px 16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                    <p style={{ margin: 0, fontWeight: 700, color: "#6e6e6e", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Summary
                    </p>

                    {state.isTimeTrigger && (
                        <div>
                            <p style={{ margin: "0 0 5px 0", fontSize: "12px", color: "#9e9e9e", fontWeight: 600 }}>
                                Trigger:
                            </p>
                            <p style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>
                                At <strong>{state.triggerTime?.slice(0, 5)}</strong>
                                {state.triggerTimezone ? ` (${state.triggerTimezone})` : ""}
                            </p>
                        </div>
                    )}

                    {state.conditions.length > 0 && (
                        <div>
                            <p style={{ margin: "0 0 5px 0", fontSize: "12px", color: "#9e9e9e", fontWeight: 600 }}>
                                {state.isGeofence
                                    ? `Geofence: ${state.geofenceDirection === "Enter" ? "first person enters" : "last person exits"}`
                                    : `Conditions${state.conditions.length > 1 ? ` (${state.conditions_logic_operator})` : ""}:`}
                            </p>
                            {!state.isGeofence && state.conditions.map((cond, i) => (
                                <p key={i} style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>
                                    {i + 1}. {state.isTimeTrigger ? "If" : "When"} <strong>{cond.pin_name ?? `Pin ${cond.pin}`}</strong> is {conditionPinStateLabel(cond, state.isLock, state.isTimeTrigger, state.triggerTime)}
                                </p>
                            ))}
                        </div>
                    )}

                    {(controlActions.length > 0 || hasAlert) && (
                        <div>
                            <p style={{ margin: "0 0 5px 0", fontSize: "12px", color: "#9e9e9e", fontWeight: 600 }}>Actions:</p>
                            {hasAlert && (
                                <p style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>🔔 Send alert notification</p>
                            )}
                            {state.selectedPinsToAffect.map((pin, i) => {
                                const action = state.actions.find(a => a.site_to === pin.DeviceUUID && a.target_output === String(pin.Pin));
                                return (
                                    <p key={i} style={{ margin: "2px 0", fontSize: "13px", color: "#3d3d3d" }}>
                                        {action?.delay_seconds > 0 ? <span style={{ color: "#9e9e9e" }}>After {action.delay_seconds}s — </span> : null}
                                        <strong>{pin.pinName}</strong> will {controlTypeLabel(action?.control_type)}
                                        {(action?.temporary_time_ms > 0) ? ` for ${action.temporary_time_ms / 1000}s` : ""}
                                    </p>
                                );
                            })}
                        </div>
                    )}

                    {state.timeframe_window_ms > 0 && (
                        <p style={{ margin: 0, fontSize: "12px", color: "#9e9e9e" }}>
                            All conditions must occur within {state.timeframe_window_ms / 1000}s of each other
                        </p>
                    )}
                </div>
            </div>

            <div className={classes.navRow}>
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={onBack} disabled={creating} />
                <Button
                    label={creating ? "Creating..." : "Create rule"}
                    icon={creating ? "pi pi-spin pi-spinner" : "pi pi-check"}
                    iconPos="right"
                    severity="success"
                    disabled={!name.trim() || creating}
                    onClick={handleCreate}
                />
            </div>
        </>
    );
};

export default WizardStepRuleInfo;
